import React, { useState, useEffect } from 'react';
import { ArrowLeft, Mail, Phone, Calendar, Trash2, Download, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout } = useAuth();

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('adminAuthToken');

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      } else if (response.status === 401) {
        // Token invalid, logout
        logout();
        navigate('/admin/login');
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to load messages');
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Error loading messages');
    }
  };

  const deleteMessage = async (messageId) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('adminAuthToken');

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        loadMessages();
        setSelectedMessage(null);
        toast.success('Pesan berhasil dihapus');
      } else if (response.status === 401) {
        logout();
        navigate('/admin/login');
        toast.error('Session expired');
      } else {
        toast.error('Failed to delete message');
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Error deleting message');
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logout berhasil');
    navigate('/admin/login');
  };

  const exportMessages = () => {
    const dataStr = JSON.stringify(messages, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `messages_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    toast.success('Data berhasil diexport');
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-300"
              >
                <ArrowLeft className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Pesan Masuk
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {messages.length} pesan total
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={exportMessages}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-300"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export JSON</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-300"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {messages.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-12 text-center">
            <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Belum Ada Pesan
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Pesan dari contact form akan muncul di sini
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Messages List */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Semua Pesan
              </h2>
              {messages.map((message, index) => (
                <div
                  key={index}
                  onClick={() => setSelectedMessage(message)}
                  className={`bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm cursor-pointer transition-all duration-300 hover:shadow-md ${
                    selectedMessage?.timestamp === message.timestamp
                      ? 'ring-2 ring-blue-600'
                      : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {message.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {message.email}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(message.created_at).split(',')[0]}
                    </span>
                  </div>
                  <p className="font-medium text-gray-800 dark:text-gray-200 mb-2">
                    {message.subject}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                    {message.message}
                  </p>
                </div>
              ))}
            </div>

            {/* Message Detail */}
            <div className="lg:sticky lg:top-8 h-fit">
              {selectedMessage ? (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
                  <div className="flex items-start justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Detail Pesan
                    </h2>
                    <button
                      onClick={() => deleteMessage(selectedMessage.id)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-300"
                      title="Hapus Pesan"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Nama Pengirim
                      </label>
                      <p className="text-gray-900 dark:text-white font-medium mt-1">
                        {selectedMessage.name}
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          Email
                        </label>
                        <p className="text-gray-900 dark:text-white">
                          <a
                            href={`mailto:${selectedMessage.email}`}
                            className="text-blue-600 hover:underline"
                          >
                            {selectedMessage.email}
                          </a>
                        </p>
                      </div>
                    </div>

                    {selectedMessage.phone && (
                      <div className="flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <div>
                          <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Nomor Telepon
                          </label>
                          <p className="text-gray-900 dark:text-white">
                            <a
                              href={`tel:${selectedMessage.phone}`}
                              className="text-blue-600 hover:underline"
                            >
                              {selectedMessage.phone}
                            </a>
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          Waktu Dikirim
                        </label>
                        <p className="text-gray-900 dark:text-white">
                          {formatDate(selectedMessage.created_at)}
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Subjek
                      </label>
                      <p className="text-gray-900 dark:text-white font-medium mt-1">
                        {selectedMessage.subject}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Pesan
                      </label>
                      <div className="mt-2 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                          {selectedMessage.message}
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <a
                        href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                        className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center space-x-2"
                      >
                        <Mail className="w-5 h-5" />
                        <span>Balas via Email</span>
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 p-12 rounded-xl shadow-sm text-center">
                  <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Pilih pesan untuk melihat detail
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMessages;
